"use client"

import { useState } from "react"
import { useRouter } from "next/navigation"
import { ArrowLeft, ArrowRight, Loader2, LogIn } from "lucide-react"
import Link from "next/link"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import { useUser } from "@/lib/hooks/use-user"
import { useUserErrorToast } from "@/lib/hooks/use-user-error-toast"
import { saveDraft } from "@/lib/mock-interview/draft"
import { DEFAULT_MOCK_INTERVIEW_LANGUAGE } from "@/lib/mock-interview/languages"
import type { ExperienceBand, MockInterviewType } from "@/lib/mock-interview/types"
import { useMockInterviewQuota, QuotaBanner } from "@/components/mock-interview/QuotaBanner"

import { JobDescriptionInput, type JobDescriptionValue } from "./JobDescriptionInput"
import { LanguagePicker } from "./LanguagePicker"
import { ResumeInput, type ResumeInputValue } from "./ResumeInput"

const EXPERIENCE_OPTIONS: ExperienceBand[] = ["0-1", "2-4", "5-8", "9+"]
const TYPE_OPTIONS: Array<{ value: MockInterviewType; label: string }> = [
  { value: "technical", label: "Technical" },
  { value: "hr", label: "HR" },
  { value: "managerial", label: "Managerial" },
]

export function PracticeSetupForm() {
  const router = useRouter()
  const {
    user,
    loading: userLoading,
    isError: userError,
    refetch: retryUser,
    refetching,
  } = useUser()
  // Stale-identity case only — the no-data case renders a full error state below.
  useUserErrorToast(userError, !!user)
  const { data: quota, isError: quotaError } = useMockInterviewQuota()

  const [role, setRole] = useState("")
  const [company, setCompany] = useState("")
  const [experienceBand, setExperienceBand] = useState<ExperienceBand>("2-4")
  const [interviewType, setInterviewType] = useState<MockInterviewType>("technical")
  const [languageCode, setLanguageCode] = useState(DEFAULT_MOCK_INTERVIEW_LANGUAGE)
  const [resume, setResume] = useState<ResumeInputValue>({
    text: null,
    resumeId: null,
    filename: null,
  })
  const [jobDescription, setJobDescription] = useState<JobDescriptionValue>({
    text: null,
    filename: null,
  })

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const outOfQuota = quota != null && quota.remaining === 0
  const resumeProvided = !!resume.resumeId

  function validate(): string | null {
    if (!role.trim()) return "Role is required."
    if (!resumeProvided) return "Resume is required — upload a PDF."
    return null
  }

  async function handleContinue() {
    // Not gated on userError: a failed background refetch keeps `user` cached
    // and the cookies valid, so the draft can still be saved for this identity.
    if (!user || userLoading || !quota || quotaError) return
    setError(null)
    const v = validate()
    if (v) {
      setError(v)
      return
    }
    // Out-of-quota users still continue, but DeviceCheck.handleBegin
    // posts demo=true and runs the 1-minute sales preview instead of a
    // real interview.
    setSubmitting(true)
    try {
      saveDraft({
        role: role.trim(),
        company: company.trim() || null,
        experience_band: experienceBand,
        interview_type: interviewType,
        language_code: languageCode,
        resume_id: resume.resumeId,
        resume_text: resume.text,
        job_description: jobDescription.text,
        job_description_filename: jobDescription.filename,
        resume_filename: resume.filename,
      })
      router.push("/mock-interview/practice/check")
    } finally {
      setSubmitting(false)
    }
  }

  if (userLoading)
    return (
      <p role="status" className="p-8 text-center">
        Checking your account…
      </p>
    )
  if (userError && !user)
    return (
      <div role="alert" className="p-8 text-center">
        Unable to verify your account.{" "}
        <Button onClick={() => void retryUser()} disabled={refetching}>
          Retry
        </Button>
      </div>
    )

  if (!user) {
    const next = encodeURIComponent("/mock-interview/practice")
    return (
      <div className="mx-auto max-w-2xl px-6 py-12">
        <Link
          href="/mock-interview"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to /mock-interview
        </Link>

        <div className="rounded-2xl border border-border bg-background p-8 text-center">
          <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <LogIn className="h-6 w-6" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight mb-2">Sign in to start the interview</h1>
          <p className="text-sm text-muted-foreground mb-6">
            AI mock interviews are tied to your account so we can save your transcript, feedback,
            and remaining credits. Sign in to continue.
          </p>
          <Link href={`/signin?next=${next}`}>
            <Button size="lg">
              <LogIn className="h-4 w-4 mr-2" />
              Sign in to continue
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl px-6 py-12">
      <Link
        href="/mock-interview"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to /mock-interview
      </Link>

      <h1 className="text-3xl font-bold tracking-tight mb-1">AI Mock Interview</h1>
      <p className="text-sm text-muted-foreground mb-6">
        30 minutes · realistic voice conversation · feedback at the end
      </p>

      <div className="mb-6">
        <QuotaBanner />
      </div>

      <div className="space-y-6">
        <div>
          <Label htmlFor="role">Role you&rsquo;re prepping for *</Label>
          <Input
            id="role"
            placeholder="e.g. Frontend Engineer"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="mt-1.5"
          />
        </div>

        <div>
          <Label htmlFor="company">Target company (optional)</Label>
          <Input
            id="company"
            placeholder="e.g. Stripe"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            className="mt-1.5"
          />
        </div>

        <div>
          <Label>Years of experience</Label>
          <div className="mt-2 grid grid-cols-4 gap-2">
            {EXPERIENCE_OPTIONS.map((band) => (
              <button
                key={band}
                type="button"
                onClick={() => setExperienceBand(band)}
                className={cn(
                  "h-10 rounded-lg border text-sm font-medium transition-colors",
                  experienceBand === band
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-border bg-background text-muted-foreground hover:text-foreground hover:bg-muted/50"
                )}
              >
                {band}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label>Interview type</Label>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {TYPE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setInterviewType(opt.value)}
                className={cn(
                  "h-10 rounded-lg border text-sm font-medium transition-colors",
                  interviewType === opt.value
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-border bg-background text-muted-foreground hover:text-foreground hover:bg-muted/50"
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label>Interview language</Label>
          <div className="mt-1.5">
            <LanguagePicker value={languageCode} onChange={setLanguageCode} />
          </div>
        </div>

        <div>
          <Label>Your resume *</Label>
          <div className="mt-1.5">
            <ResumeInput value={resume} onChange={setResume} disabled={submitting} />
          </div>
        </div>

        <div>
          <Label>Job description (optional)</Label>
          <div className="mt-1.5">
            <JobDescriptionInput
              value={jobDescription}
              onChange={setJobDescription}
              disabled={submitting}
            />
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-950/30 px-4 py-2.5 text-sm text-red-700 dark:text-red-200">
            {error}
          </div>
        )}

        <Button
          type="button"
          size="lg"
          disabled={submitting || !quota || quotaError}
          onClick={handleContinue}
          className="w-full"
        >
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              Continue to device check
              <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>

        <p className="text-xs text-muted-foreground text-center">
          {outOfQuota
            ? "No credits available — this will run as a 1-minute demo. No credit will be deducted."
            : "1 mock-interview credit will be deducted when you click Begin on the next screen."}
        </p>
      </div>
    </div>
  )
}
